import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MpesaTransaction } from "@/hooks/useMpesaTransactions";
import { format } from "date-fns";

interface MpesaTransactionsTableProps {
  transactions: MpesaTransaction[];
  isLoading: boolean;
}

export const MpesaTransactionsTable: React.FC<MpesaTransactionsTableProps> = ({
  transactions,
  isLoading
}) => {
  const getStatusBadge = (status: string) => {
    const variants = {
      completed: "success" as const,
      pending: "default" as const,
      failed: "destructive" as const,
      cancelled: "secondary" as const,
    };
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || "secondary"}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  const formatTransactionDate = (timestamp?: number) => {
    if (!timestamp) return '-';
    
    // Convert MPesa timestamp format (20191219102115) to readable date
    const dateStr = timestamp.toString();
    if (dateStr.length === 14) {
      const year = dateStr.slice(0, 4);
      const month = dateStr.slice(4, 6);
      const day = dateStr.slice(6, 8);
      const hour = dateStr.slice(8, 10);
      const minute = dateStr.slice(10, 12);
      const second = dateStr.slice(12, 14);
      
      const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
      return format(date, 'MMM dd, yyyy HH:mm:ss');
    }
    
    return dateStr;
  };

  const formatAmount = (amount?: number) => {
    if (!amount) return '-';
    return `KSh ${amount.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <p className="text-gray-500">Loading transactions...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent MPesa Transactions</CardTitle>
        <p className="text-sm text-gray-600">
          Latest {transactions.length} transactions received via callback
        </p>
      </CardHeader>
      <CardContent>
        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No transactions found</p>
            <p className="text-sm text-gray-400 mt-2">
              Transactions will appear here after MPesa callbacks are received
            </p>
          </div>
        ) : (
          <ScrollArea className="h-96">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Receipt #</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Transaction Date</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {getStatusBadge(transaction.status)}
                    </TableCell>
                    <TableCell className="font-mono">
                      {formatAmount(transaction.amount)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {transaction.mpesa_receipt_number || '-'}
                    </TableCell>
                    <TableCell className="font-mono">
                      {transaction.phone_number || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatTransactionDate(transaction.transaction_date)}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className={`${transaction.result_code === 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {transaction.result_code}: {transaction.result_desc}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {format(new Date(transaction.created_at), 'MMM dd, HH:mm')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
